import {
  EXTERNAL_LINKS,
  PRIVATE_ROUTES,
  PRODUCTS,
  REDIRECT_TO_URL_PATH_REGEX,
  ROUTES,
} from './constants'

/** Whether `redirectUrl` points at the Play app (same origin as `playBaseUrl`). */
function isRedirectTargetPlayApp(
  redirectUrl: string,
  playBaseUrl: string,
  pageOrigin: string
): boolean {
  try {
    const playOrigin = new URL(playBaseUrl).origin
    const targetOrigin = new URL(redirectUrl, pageOrigin).origin
    return targetOrigin === playOrigin
  } catch {
    return false
  }
}

export function logout(redirectTo = '/') {
  if (
    window.location.pathname.includes(ROUTES.logout) ||
    window.location.pathname.includes(ROUTES.login)
  ) {
    return
  }

  window.location.href = `${ROUTES.logout}?redirectTo=${redirectTo}`
}

// A relative redirect target points at the marketplace (or Play for
// ai-business), except for pages this app serves itself — the OAuth consent
// screen — which would otherwise land on the marketplace's 404.
export function getRedirectBaseUrl(redirectTo: string, product: string) {
  if (redirectTo.startsWith(ROUTES.oauthConsent)) {
    return window.location.origin
  }

  return product === PRODUCTS.aiBusiness
    ? EXTERNAL_LINKS.playMydra
    : EXTERNAL_LINKS.marketplace
}

export function isPrivateRoute(route: string) {
  return PRIVATE_ROUTES.some((privateRoute: string | RegExp) => {
    if (privateRoute instanceof RegExp) {
      return privateRoute.exec(route)
    }

    return privateRoute === route
  })
}

export function isValidRedirect(path: string) {
  // allow internal paths that match our path regex
  const isInternalPath =
    path &&
    ![
      ROUTES.login,
      ROUTES.authLogin,
      ROUTES.signup,
      ROUTES.accessDenied,
      ROUTES.notFound,
    ].includes(path) &&
    path.match(REDIRECT_TO_URL_PATH_REGEX)

  // allow full URLs that belong to mydra.io domain
  const isMydraDomain =
    path && /^https:\/\/(?:[\w-]+\.)*mydra\.io(?:\/.*)?$/.test(path)

  // allow localhost URLs for local development
  const isLocalhost =
    path && /^http:\/\/localhost(?::\d+)?(?:\/.*)?$/.test(path)

  return isInternalPath || isMydraDomain || isLocalhost
}

/**
 * Ai-business: Play base if no safe redirect; if redirect targets Play, use it
 * (e.g. deep links); otherwise Play base.
 */
export function resolveAiBusinessRedirectDestination(
  playUrl: string,
  redirectCandidate: string | undefined,
  pageOrigin: string
): string {
  if (
    redirectCandidate &&
    isValidRedirect(redirectCandidate) &&
    isRedirectTargetPlayApp(redirectCandidate, playUrl, pageOrigin)
  ) {
    return new URL(redirectCandidate, pageOrigin).href
  }
  return playUrl
}
